<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="'' + jsa.jsa_name + ' [JSA] :: MeritEHS'"/>

            <div class="container d-print-none">
                <h4 class="py-3 mb-4">
                    <ol class="breadcrumb font-weight-bold m-0">
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/jsa`">
                                <i class="fa fa-files-o"></i> JSA
                            </router-link>
                        </li>
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/jsa/view/${jsa_id}`">
                                {{ jsa.jsa_name }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active">
                            <i class="fa fa-file-pdf-o"></i> PDF
                        </li>
                    </ol>
                </h4>
            </div>
            <div class="container bg-white d-print-none py-4 mb-4">
                <div class="alert alert-info">
                    <strong>How to Export as PDF:</strong>
                    <ol>
                        <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                        <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                        <li>Select "Save as PDF" or "Adobe PDF".</li>
                        <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                    </ol>

                    <div class="mt-3">
                        <strong>PDF Options:</strong>
                        <ol>
                            <li class="mb-1" v-if="pdfLogo">
                                <span>Show/Hide Logo:</span>
                                <select v-model="showLogo">
                                    <option :value="true">Show Logo</option>
                                    <option :value="false">Hide Logo</option>
                                </select>
                                <span class="small">Show/Hide your company logo on the JSA PDF.</span>
                            </li>
                            <li class="mb-1" v-if="pdfLogo">
                                <span>Show Employee Signature & Date:</span>
                                <select v-model="showEmpSigAndDate">
                                    <option :value="true">Show Employee Signature & Date</option>
                                    <option :value="false">Hide Employee Signature & Date</option>
                                </select>
                                <span class="small">Show/Hide Employee Signature & Date.</span>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

            <div class="container bg-white py-4">
                <div class="row" v-if="pdfLogo && showLogo === true" style="margin-bottom: 50px;">
                    <div class="col-3">
                        <img :src="pdfLogo" class="img-fluid" />
                    </div>
                </div>

                <h4 class="mb-0">
                    JSA: {{ jsa.jsa_name }}
                </h4>
                <hr>
                <div class="row mb-2">
                    <div class="col text-muted">Status</div>
                    <div class="col-8">{{ jsa.status_name }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">JSA Name</div>
                    <div class="col-8">{{ jsa.jsa_name }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">JSA Date</div>
                    <div class="col-8">{{ jsa.jsa_date_formatted }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">User</div>
                    <div class="col-8"><user-name :user="jsa" :plainText="true" /></div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Jobsite</div>
                    <div class="col-8">
                        <router-link :to="`/jobsites/view/${jsa.JobsiteId}`">
                            {{ jsa.JobsiteNumber }} - {{ jsa.JobsiteName }}
                        </router-link>
                    </div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Contractor</div>
                    <div class="col-8">{{ jsa.jsa_contractor }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Total # of Steps</div>
                    <div class="col-8">{{ totalSteps }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Total # of Hazards</div>
                    <div class="col-8">{{ totalHazards }}</div>
                </div>

                <hr/>

                <div class="row mb-2">
                    <div class="col text-muted">Building/Area:</div>
                    <div class="col-8">{{ jsa.jsa_building_area }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Description of Work:</div>
                    <div class="col-8">{{ jsa.jsa_description }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Standard PPE:</div>
                    <div class="col-8">{{ jsa.jsa_safety_equipment }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Specialized PPE:</div>
                    <div class="col-8">{{ jsa.jsa_specialized_equipment }}</div>
                </div>
                <div class="row mb-2">
                    <div class="col text-muted">Monitoring Activities:</div>
                    <div class="col-8">{{ jsa.jsa_monitoring_act }}</div>
                </div>

                <hr/>

                <strong slot="header">Steps and Hazards</strong>
                <ol>
                   <li v-for="step in jsa.jsa_steps" class="mb-2">
                        {{ step.step_label }}
                        <ul>
                           <li v-for="hazard in step.hazards" class="mb-1">
                                <span :class="`text-${getRacLevelColor(hazard.rac_level)}`">{{ hazard.rac_level }}</span>
                                {{ hazard.hazard }}
                                |
                                <span class="text-primary" title="Standard Controls">
                                    <template v-for="(control, index) in hazard.controls"><template v-if="index > 0">, </template>{{ control }}</template>
                                    <template v-if="hazard.other_controls">{{ hazard.other_controls }}</template>
                                </span>
                           </li>
                        </ul>
                   </li>
                </ol>

                <div v-if="employees.length > 0">
                    <hr/>
                    <strong slot="header">Employees ({{ employees.length }})</strong>

                    <ul>
                       <li v-for="(emp, i) in employees" :class="{'mt-4': (showEmpSigAndDate && i > 0)}">
                            {{ emp.FirstName }} {{ emp.LastName }}<span v-if="showEmpSigAndDate && emp.pivot.employee_signed_at">, Signed At: {{ emp.pivot.employee_signed_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>

                            <span v-if="showEmpSigAndDate && emp.pivot.signature_file_id">
                                <img v-bind:src="getSignature(emp.pivot.signature_file_id)" class="img-fluid" :ref="`file-${emp.pivot.signature_file_id}`" />
                            </span>
                       </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                loading: true,
                pdfLogo: null,
                showLogo: true,
                showEmpSigAndDate: false,
                userTimezone: this.$store.state.user.timezone,
                jsa_id: this.$route.params.id,
                jsa: {},
                employees: [],
            }
        },

        computed: {
            totalSteps(){
                //Send it
                return this.jsa.jsa_steps.length;
            },

            totalHazards(){
                //Start it
                var t = 0;

                //Loop and count
                for (var i = this.jsa.jsa_steps.length - 1; i >= 0; i--)
                {
                    t += this.jsa.jsa_steps[i].hazards.length;
                }

                //Send it
                return t;
            },
        },

        methods: {
            getRacLevelColor(level){
                //switch
                switch(level)
                {
                    case 'Extremely High':
                        return 'danger';
                        break;
                    case 'High':
                        return 'orange';
                        break;
                    case 'Medium':
                        return 'warning';
                        break;
                    case 'Low':
                        return 'success';
                        break;

                }

                return 'success';
            },

            getJsa() {
                //Get the data
                this.$http
                    .get('jsa/view/' + this.jsa_id, {include_employees: true, include_pdf: true})
                    .then(response => {
                        //Set all the data
                        this.jsa = response.data.jsa;
                        this.employees = response.data.employees;
                        this.pdfLogo = response.data.pdfLogo;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            getSignature(file_id){
                //Send it
                this.$http.get('files/view/'+file_id, {display_type: 'fluid'})
                    .then(response => {
                        //Sent the ref
                        this.$refs['file-' + file_id][0].src = response.data;
                    })
                    .catch(err => {
                        switch(err.status)
                        {
                            case 410:
                                //Stop here?
                                //this.$refs['file-' + file.file_id][0].src = '/Images/file-not-found.png';
                                break;
                        }
                    });
            },

        },
        mounted() {
            //Get the JSA
            this.getJsa();
        }
    }
</script>
<style scoped>
.text-orange {
    color: #ef871d;
}
.break-protect {
    page-break-inside: avoid;
}
.body {
    margin: 25mm;
}
@page {
    margin-top: 1in;
    margin-bottom: 1in;
}
</style>